import { v4 as uuidV4 } from 'uuid';
import {
  AchievementRewardType,
  AchievementVisibility,
  GAME_PASS_ACHIEVEMENT_NAME,
  IAchievementGetDTO,
  IAchievementPostDTO,
  INotification,
} from 'dorian-shared/types/achievement/Achievement';
import { Story, EpisodeRunners } from '../../../dorian-shared/types/story/Story';
import { IAchievementModal } from './AchievementsModalTypes';

const initialNotification: INotification = {
  title: '',
  message: '',
};

export const newAchievement = (id: number|string = uuidV4()): IAchievementModal => ({
  id,
  name: '',
  description: '',
  displayName: '',
  icon: '',
  isAdded: true,
  isManual: false,
  unavailableAfter: undefined,
  hidden: AchievementVisibility.Visible,
  check: {
    variableId: 0,
    operator: '',
    value: '',
  },
  usedInStory: [],
  isOwnedByUser: false,
  notification: initialNotification,
  rewards: [{
    contentId: '',
    type: 0,
    delaySec: 0,
    achievementLabel: 'Bonus',
  }],
});

export const convertAchievementToPostDTO = (achievement: IAchievementModal | IAchievementGetDTO): IAchievementPostDTO => {
  const { notification } = achievement;
  if (notification) {
    notification.title = notification.title?.trim() ?? '';
    notification.message = notification.message?.trim() ?? '';
  }

  // Get the first user-visible reward (not HasAchievement)
  const firstUserVisibleReward = getFirstUserVisibleReward(achievement);
  const isRewardContentNone = firstUserVisibleReward?.contentId === ''
    || firstUserVisibleReward?.contentId === null
    || firstUserVisibleReward?.contentId === undefined;

  const iconId = achievement.icon === '' ? null : achievement.icon;

  return {
    name: achievement.name,
    icon: iconId,
    displayName: achievement.displayName,
    description: achievement.description,
    isManual: achievement.isManual,
    check: { ...achievement.check },
    unavailableAfter: achievement.unavailableAfter,
    hidden: achievement.hidden,
    notification,
    rewards: [{
      contentId: isRewardContentNone
        ? null
        : firstUserVisibleReward?.contentId ?? null,
      delaySec: firstUserVisibleReward?.delaySec ?? 0,
      achievementLabel: firstUserVisibleReward?.achievementLabel ?? 'Bonus',
      type: firstUserVisibleReward?.type ?? AchievementRewardType.None,
    }],
  };
};

export const convertAchievementGetDTOToModal = (achievement: IAchievementGetDTO): IAchievementModal => {
  const { notification } = achievement;
  if (notification) {
    notification.title = notification.title?.trim() ?? '';
    notification.message = notification.message?.trim() ?? '';
  }

  // Filter out HasAchievement rewards - these are auto-managed by the backend
  // and should not be shown or edited in the modal
  const userVisibleRewards = achievement.rewards?.filter(
    (r) => r.type !== AchievementRewardType.HasAchievement,
  ) ?? [];

  return {
    id: achievement.id,
    name: achievement.name ?? '',
    icon: achievement.icon ?? '',
    displayName: achievement.displayName ?? '',
    description: achievement.description ?? '',
    isManual: achievement.isManual ?? false,
    check: {
      ...achievement.check,
      value: achievement.check.value,
    },
    isAdded: false,
    usedInStory: achievement.usedInStory ?? [],
    isOwnedByUser: achievement.isOwnedByUser ?? false,
    unavailableAfter: achievement.unavailableAfter,
    hidden: achievement.hidden,
    notification,
    rewards: userVisibleRewards.length
      ? userVisibleRewards.map((r) => ({ ...r }))
      : [{
        contentId: '', type: 0, delaySec: 0, achievementLabel: 'Bonus',
      }],

  };
};

export const isGamePassAchievement = (achievement: IAchievementModal): boolean => achievement.name === GAME_PASS_ACHIEVEMENT_NAME;

export const findGamePassIndex = (achievements: IAchievementModal[]): number => achievements.findIndex(isGamePassAchievement);

export const getEpisodeTitleIcons = (episode: Story) => {
  let icons = '';
  if (episode.isBonus) {
    icons += ' 🎁';
  }
  if (episode.isLocked) {
    icons += ' 🔒';
  }

  if (episode.runner === EpisodeRunners.RenPy) {
    icons += ' 🛫';
  }

  return icons;
};

/**
 * Gets the first user-visible reward from an achievement.
 * Filters out auto-generated HasAchievement rewards.
 */
export const getFirstUserVisibleReward = (achievement: IAchievementModal | IAchievementGetDTO) => {
  return achievement.rewards?.find(
    (reward) => reward.type !== AchievementRewardType.HasAchievement,
  ) ?? null;
};

/**
 * Gets the index of the first user-visible reward in the rewards array.
 * Returns -1 if no user-visible reward is found.
 */
export const getFirstUserVisibleRewardIndex = (achievement: IAchievementModal | IAchievementGetDTO): number => {
  return achievement.rewards?.findIndex(
    (reward) => reward.type !== AchievementRewardType.HasAchievement,
  ) ?? -1;
};

